import { useEffect, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion, type PanInfo } from 'framer-motion'
import { cn } from '@/lib/utils'

interface AperitifItem {
  name: string
  image: string
}

interface AperitifsSectionProps {
  id?: string
  label?: string
  title: string
  text: string
  items: AperitifItem[]
  badges?: string[]
}

export function AperitifsSection({
  id,
  label,
  title,
  text,
  items,
  badges,
}: AperitifsSectionProps) {
  const shouldReduceMotion = useReducedMotion()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isHovering, setIsHovering] = useState(false)

  useEffect(() => {
    if (isHovering || items.length === 0) return
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length)
    }, 4200)
    return () => clearInterval(interval)
  }, [isHovering, items.length])

  const activeItem = items[activeIndex]

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const handleActivate = (index: number) => {
    setIsHovering(true)
    setActiveIndex(index)
  }

  const handleDeactivate = () => setIsHovering(false)

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const offsetThreshold = 50
    const velocityThreshold = 500
    if (info.offset.x > offsetThreshold || info.velocity.x > velocityThreshold) {
      setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1))
    } else if (info.offset.x < -offsetThreshold || info.velocity.x < -velocityThreshold) {
      setActiveIndex((prev) => (prev + 1) % items.length)
    }
  }

  const paragraphs = text
    .replace(/\\n/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')

  return (
    <section
      id={id}
      className="relative overflow-hidden bg-bar-light px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8"
      aria-labelledby={id ? `${id}-heading` : undefined}
    >
      <div className="relative mx-auto max-w-6xl">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Copy */}
          <motion.div
            {...fadeUp}
            className="order-2 lg:order-1 lg:col-span-7"
          >
            {label && (
              <span className="mb-4 inline-block text-xs font-extrabold uppercase tracking-[0.2em] text-ink/70">
                {label}
              </span>
            )}

            <h2
              id={id ? `${id}-heading` : undefined}
              className="font-display text-[clamp(3rem,11vw,7rem)] font-extrabold leading-[0.85] tracking-tighter text-ink"
            >
              {title}
            </h2>

            {/* Liquid underline */}
            <div className="relative mt-3 h-6 w-[clamp(8rem,28vw,18rem)] sm:mt-4">
              <svg
                viewBox="0 0 240 24"
                fill="none"
                aria-hidden="true"
                className="h-full w-full"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 14c18-8 34-8 52 0s34 8 52 0 34-8 52 0 34 8 52 0 34-8 52 0"
                  stroke="#1a1816"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                  initial={shouldReduceMotion ? {} : { pathLength: 0, opacity: 0 }}
                  whileInView={shouldReduceMotion ? {} : { pathLength: 1, opacity: 1 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 1, ease: 'easeInOut', delay: 0.2 }}
                />
              </svg>
            </div>

            <div className="mt-8 max-w-xl space-y-5 text-lg leading-relaxed text-ink/90 sm:text-xl">
              {paragraphs.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>

            {badges && badges.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {badges.map((badge, index) => (
                  <motion.span
                    key={index}
                    variants={{
                      hidden: { opacity: 0, y: 16 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                    className="inline-flex items-center rounded-full border border-ink/20 bg-white/40 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-ink backdrop-blur-sm"
                  >
                    {badge}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>

          {/* Photo frame */}
          <motion.div
            {...fadeUp}
            className="order-1 lg:order-2 lg:col-span-5"
          >
            <div className="relative mx-auto max-w-sm lg:max-w-none">
              {/* Back polaroid layer */}
              <div
                className="absolute inset-0 rotate-3 rounded-[2rem] bg-ink/10"
                aria-hidden="true"
              />
              {/* Main frame */}
              <div
                className="relative -rotate-2 rounded-[2rem] border-4 border-white bg-white p-3 shadow-2xl"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={handleDeactivate}
              >
                <motion.div
                  className="relative aspect-[4/5] w-full overflow-hidden rounded-[1.5rem] bg-bar-dark/20"
                  aria-live="polite"
                  aria-atomic="true"
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragStart={() => setIsHovering(true)}
                  onDragEnd={(e, info) => {
                    handleDragEnd(e, info)
                    setIsHovering(false)
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeItem?.image}
                      src={activeItem?.image}
                      alt={activeItem?.name}
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: shouldReduceMotion ? 0 : 0.45 }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* Dots */}
              <div className="relative z-10 mt-6 flex justify-center gap-2.5">
                {items.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={`Mostra ${item.name}`}
                    aria-current={index === activeIndex ? 'true' : undefined}
                    onClick={() => handleActivate(index)}
                    onMouseEnter={() => handleActivate(index)}
                    onMouseLeave={handleDeactivate}
                    className={cn(
                      'h-3 w-3 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-bar-light',
                      index === activeIndex
                        ? 'scale-110 bg-ink'
                        : 'bg-ink/30 hover:bg-ink/55'
                    )}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
